/**
 * Библиотека: загрузка фото и их плитка.
 *
 * Фото кладут тремя путями, и все три ведут в одну ручку загрузки: кнопка
 * выбора файлов, перетаскивание на страницу и Ctrl-V из буфера (скриншот,
 * картинка из браузера). Битый файл не отменяет остальные — сервис вернёт его
 * строкой в `errors`, она показывается как есть.
 *
 * Плитки, выделение и действия над ними — в `ReferenceBrowser`.
 *
 * «Поиск» (по описанию, фото, платью) — `PhotoSearchPanel`: пока панель открыта, вставка и перетаскивание
 * картинки — запрос поиска, а не загрузка (обработчики окна ниже смотрят `searchOpen`);
 * кнопка «Добавить фото» грузит как обычно. Есть выдача — вместо фото библиотеки её плитка.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ImagePlus, Pencil, ScanSearch, Trash2, Upload } from "lucide-react";
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router";

import { LibNav } from "@/components/library/LibNav";
import { LibraryDialog } from "@/components/library/LibraryDialog";
import { PhotoSearchPanel, type SimilarFound } from "@/components/library/PhotoSearchPanel";
import { ReferenceBrowser } from "@/components/library/ReferenceBrowser";
import { btn, btnGhost, btnPrimary, ErrorLine, errorText, Loading, Panel } from "@/components/trends/ui";
import { deleteLibrary, libraryKeys, loadLibrary, plural, uploadPhotos, type UploadResult } from "@/lib/library";
import { num } from "@/lib/media";
import { scoreMap, showsPercent } from "@/lib/similar";
import { retryQuery } from "@/lib/trends";

export function LibraryPage() {
  const libraryId = Number(useParams().libraryId);
  const valid = Number.isFinite(libraryId) && libraryId > 0;
  const queryClient = useQueryClient();
  const fileInput = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  const [report, setReport] = useState<UploadResult | null>(null);
  const [editing, setEditing] = useState(false);
  const navigate = useNavigate();
  // Ссылка с `?similar_dress=` / `?similar_text=` открывает экран сразу с поиском.
  const [params] = useSearchParams();
  const [searchOpen, setSearchOpen] = useState(() => params.has("similar_dress") || params.has("similar_text"));
  const [found, setFound] = useState<SimilarFound | null>(null);
  const scores = useMemo(() => (found ? scoreMap(found.view.items) : null), [found]);

  const detail = useQuery({
    queryKey: libraryKeys.one(libraryId),
    queryFn: () => loadLibrary(libraryId),
    retry: retryQuery,
    enabled: valid,
  });

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: libraryKeys.one(libraryId) });
    void queryClient.invalidateQueries({ queryKey: libraryKeys.list });
  };

  const remove = useMutation({
    mutationFn: () => deleteLibrary(libraryId),
    onSuccess: () => {
      navigate("/lib");
      // Запрос самой библиотеки не перезапрашиваем — она уже удалена (404);
      // корзины и «Все референсы» теряют её строки, их перезапрашиваем.
      queryClient.removeQueries({ queryKey: libraryKeys.one(libraryId) });
      void queryClient.invalidateQueries({ queryKey: libraryKeys.list });
      void queryClient.invalidateQueries({ queryKey: ["library"] });
      void queryClient.invalidateQueries({ queryKey: ["project"] });
    },
  });

  const upload = useMutation({
    mutationFn: (files: File[]) => uploadPhotos(libraryId, files),
    onMutate: () => setReport(null),
    onSuccess: (got) => {
      setReport(got);
      refresh();
    },
  });

  // Держим свежую функцию загрузки в ref: слушатели окна вешаются один раз.
  const send = useRef<(files: File[]) => void>(() => undefined);
  const canUpload = valid && detail.isSuccess && !upload.isPending;
  const { mutate } = upload;
  // Открыт поиск — вставку и перетаскивание забирает его панель, сюда они не доходят.
  const searching = useRef(searchOpen);
  useLayoutEffect(() => {
    send.current = (files) => {
      if (files.length && canUpload) mutate(files);
    };
    searching.current = searchOpen;
  }, [canUpload, mutate, searchOpen]);

  // Перетаскивание на страницу и вставка из буфера — на всё окно: целиться в
  // маленькую зону неудобно, а других приёмников файлов на экране нет.
  useEffect(() => {
    let depth = 0;
    const hasFiles = (e: DragEvent) =>
      !searching.current && Array.from(e.dataTransfer?.types ?? []).includes("Files");
    const onEnter = (e: DragEvent) => {
      if (!hasFiles(e)) return;
      depth += 1;
      setDragging(true);
    };
    const onOver = (e: DragEvent) => {
      if (!hasFiles(e)) return;
      e.preventDefault(); // без этого браузер не даст бросить файл и откроет его сам
      if (e.dataTransfer) e.dataTransfer.dropEffect = "copy";
    };
    const onLeave = (e: DragEvent) => {
      if (!hasFiles(e)) return;
      depth = Math.max(0, depth - 1);
      if (depth === 0) setDragging(false);
    };
    const onDrop = (e: DragEvent) => {
      if (!hasFiles(e)) return;
      e.preventDefault();
      depth = 0;
      setDragging(false);
      send.current(Array.from(e.dataTransfer?.files ?? []));
    };
    const onPaste = (e: ClipboardEvent) => {
      if (searching.current) return;
      const files = Array.from(e.clipboardData?.items ?? [])
        .filter((item) => item.kind === "file" && item.type.startsWith("image/"))
        .map((item) => item.getAsFile())
        .filter((f): f is File => f !== null);
      // Текст в поле заметки вставляется как обычно — перехватываем только картинки.
      if (!files.length) return;
      e.preventDefault();
      send.current(files);
    };
    window.addEventListener("dragenter", onEnter);
    window.addEventListener("dragover", onOver);
    window.addEventListener("dragleave", onLeave);
    window.addEventListener("drop", onDrop);
    window.addEventListener("paste", onPaste);
    return () => {
      window.removeEventListener("dragenter", onEnter);
      window.removeEventListener("dragover", onOver);
      window.removeEventListener("dragleave", onLeave);
      window.removeEventListener("drop", onDrop);
      window.removeEventListener("paste", onPaste);
    };
  }, []);

  if (!valid)
    return (
      <div className="p-5">
        <ErrorLine error={new Error("Неверный адрес библиотеки.")} />
      </div>
    );
  if (detail.isPending) return <Loading />;
  if (detail.isError)
    return (
      <div className="grid gap-3 p-5">
        <LibNav current="library" />
        <ErrorLine error={detail.error} />
      </div>
    );

  const { library, items } = detail.data;

  const confirmDelete = () => {
    const n = library.photos;
    const m = library.used;
    const text =
      `Удалить библиотеку «${library.name}» с ${n} ${plural(n, "референсом", "референсами", "референсами")}? ` +
      `Они уйдут из корзин и из ${m} ${plural(m, "проекта", "проектов", "проектов")}; файлы останутся в хранилище.` +
      (library.generations > 0
        ? ` ${library.generations} ${plural(library.generations, "генерация останется", "генерации останутся", "генераций останутся")} в карточках платьев.`
        : "");
    if (window.confirm(text)) remove.mutate();
  };

  return (
    <div className="grid gap-4 p-5">
      <LibNav current="library" />

      <header className="grid gap-1" data-annot="шапка библиотеки">
        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
          <h1 className="text-[18px] font-semibold tracking-[-0.01em] text-fg">{library.name}</h1>
          <span className="rounded-[4px] bg-neutral-bg px-1.5 text-[11.5px] text-fg-muted">{library.category_name}</span>
          <span className="text-[12.5px] text-fg-faint tnum">{library.photos} фото</span>
          <div className="ml-auto flex items-center gap-2">
            {!searchOpen ? (
              <button type="button" className={btnPrimary} onClick={() => setSearchOpen(true)}>
                <ScanSearch size={14} />
                Поиск
              </button>
            ) : null}
            <button type="button" className={btn} onClick={() => setEditing(true)}>
              <Pencil size={14} />
              Править
            </button>
            <button
              type="button"
              className={`${btnGhost} hover:text-err`}
              onClick={confirmDelete}
              disabled={remove.isPending}
            >
              <Trash2 size={14} />
              Удалить
            </button>
          </div>
        </div>
        {remove.isError ? <ErrorLine error={remove.error} /> : null}
        {library.note ? <p className="text-[12.5px] text-fg-muted">{library.note}</p> : null}
      </header>

      {searchOpen ? (
        <PhotoSearchPanel libraryId={libraryId} onResult={setFound} onClose={() => setSearchOpen(false)} />
      ) : null}

      <Panel annot="загрузка фото">
        <div className="flex flex-wrap items-center gap-3 px-3 py-2">
          <input
            ref={fileInput}
            type="file"
            accept="image/*"
            multiple
            className="hidden"
            onChange={(e) => {
              send.current(Array.from(e.target.files ?? []));
              e.target.value = ""; // тот же файл ещё раз — тоже событие
            }}
          />
          <button
            type="button"
            className={btn}
            onClick={() => fileInput.current?.click()}
            disabled={upload.isPending}
          >
            <ImagePlus size={14} />
            {upload.isPending ? "Загружаю…" : "Добавить фото"}
          </button>
          <span className="text-[12px] text-fg-faint">
            {searchOpen
              ? "пока открыт поиск по фото, Ctrl-V и перетаскивание идут в поиск"
              : "или перетащите на страницу, или Ctrl-V"}
          </span>
        </div>
        {upload.isError || report ? (
          <div className="grid gap-1.5 border-t border-border px-3 py-2 text-[12.5px]">
            {report ? (
              <div className={report.added ? "text-ok" : "text-fg-muted"}>
                Добавлено фото: {report.added}
                {report.errors.length ? `, не принято ${report.errors.length}` : ""}.
              </div>
            ) : null}
            {report?.errors.length ? (
              <ul className="grid gap-0.5 text-err">
                {report.errors.map((e, i) => (
                  <li key={i} className="break-words">
                    {e}
                  </li>
                ))}
              </ul>
            ) : null}
            {upload.isError ? <div className="text-err">{errorText(upload.error)}</div> : null}
          </div>
        ) : null}
      </Panel>

      {found && scores ? (
        <ReferenceBrowser
          key="similar"
          items={found.view.items}
          mode="library"
          queryKey={found.queryKey}
          resetKey={JSON.stringify(found.queryKey)}
          scores={scores}
          percent={showsPercent(found.view)}
          title={
            <>
              {showsPercent(found.view) ? "Похожие" : "Найдено"} {found.label} <span className="tnum text-fg-faint">{num(found.view.items.length)}</span>
            </>
          }
          emptyText="Похожих не нашлось."
        />
      ) : (
        <ReferenceBrowser
          key="library"
          libraryId={libraryId}
          items={items}
          mode="library"
          queryKey={libraryKeys.one(libraryId)}
          emptyText="Фото пока нет — добавьте кнопкой, перетаскиванием или Ctrl-V."
        />
      )}

      <LibraryDialog
        open={editing}
        onClose={() => setEditing(false)}
        library={library}
        onDone={refresh}
      />

      {dragging ? (
        <div className="pointer-events-none fixed inset-0 z-50 grid place-items-center bg-bg/80 backdrop-blur-[2px]">
          <div className="grid justify-items-center gap-2 rounded-[var(--radius-card)] border-2 border-dashed border-accent bg-bg-elevated px-10 py-8 text-[14px] text-fg">
            <Upload size={28} className="text-accent" />
            Отпустите — фото лягут в «{library.name}»
          </div>
        </div>
      ) : null}
    </div>
  );
}
